import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import { Users, X } from 'lucide-react';
import { api, ApiError, type Org, type OrgRole, type Person, type Session } from '../../lib/api';
import { useRefreshSession } from '../../lib/session';
import { Avatar, toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import { ActivityLog } from '../../ui/ActivityLog';
import { OrgAgents } from './OrgAgents';
import { OrgGuests } from './OrgGuests';

interface MembersRes {
  members: (Person & { role: OrgRole })[];
  invites: { id: string; email: string; role: OrgRole }[];
}

interface OrgTeam {
  id: string;
  name: string;
  members: number;
  boards: number;
  joined: boolean;
}

const report = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));

export function OrgSettings({ org, session }: { org: Org; session: Session }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refresh = useRefreshSession();
  const [, setParams] = useSearchParams();
  const admin = org.role !== 'member';
  const me = session.user!;
  const members = useQuery({ queryKey: ['org-members', org.id], queryFn: () => api.get<MembersRes>(`/orgs/${org.id}/members`) });
  const teams = useQuery({ queryKey: ['org-teams', org.id], queryFn: () => api.get<OrgTeam[]>(`/orgs/${org.id}/teams`) });
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [name, setName] = useState(org.name);
  const location = useLocation();
  // "Members" in the organization's menu leads straight to them.
  useEffect(() => {
    if (location.hash === '#members') document.getElementById('members')?.scrollIntoView({ block: 'start' });
  }, [location.hash, members.data]);
  const [domain, setDomain] = useState(org.allowedDomains.join(', '));

  const reload = () => {
    void qc.invalidateQueries({ queryKey: ['org-members', org.id] });
    void refresh();
  };

  const invite = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length) return;
    try {
      await api.post(`/orgs/${org.id}/invites`, { emails: list, role });
      setEmails('');
      toast(t('org.invited'));
      reload();
    } catch (e) {
      report(e);
    }
  };

  return (
    <>
      <div className="main-head">
        <h1>{org.name}</h1>
      </div>

      {admin && (
        <div className="settings-card">
          <h3>{t('org.name')}</h3>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            <button
              className="btn"
              disabled={!name.trim() || name === org.name}
              onClick={() =>
                api
                  .patch(`/orgs/${org.id}`, { name })
                  .then(() => refresh())
                  .catch(report)
              }
            >
              {t('common.save')}
            </button>
          </div>
          <h3 style={{ marginTop: 20 }}>{t('org.domains')}</h3>
          <p className="muted" style={{ margin: '0 0 12px' }}>
            {t('org.domainsHint')}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={me.email.split('@')[1]} />
            <button
              className="btn"
              onClick={() =>
                api
                  .patch(`/orgs/${org.id}`, { allowedDomains: domain.split(/[\s,;]+/).filter(Boolean) })
                  .then(() => {
                    toast(t('common.done'));
                    void refresh();
                  })
                  .catch(report)
              }
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      )}

      <div className="settings-card" id="members">
        <h3>{t('org.members')}</h3>
        {admin && (
          <div style={{ display: 'grid', gap: 8, margin: '12px 0 16px' }}>
            <textarea className="input" style={{ height: 72, padding: 10, resize: 'vertical' }} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder={t('org.invitePlaceholder')} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <select className="input" style={{ width: 200 }} value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
                <option value="member">{t('org.roles.member')}</option>
                <option value="admin">{t('org.roles.admin')}</option>
                {org.role === 'owner' && <option value="owner">{t('org.roles.owner')}</option>}
              </select>
              <button className="btn primary" onClick={invite} disabled={!emails.trim()}>
                {t('org.invite')}
              </button>
            </div>
          </div>
        )}
        {members.data?.members.map((m) => (
          <div className="person-row" key={m.id}>
            <Avatar name={m.name} color={m.color} size={32} />
            <div className="who">
              <div style={{ fontWeight: 600 }}>
                {m.name} {m.id === me.id && <span className="subtle">({t('common.you')})</span>}
              </div>
              <div className="subtle" style={{ fontSize: 'var(--fs-2)' }}>
                {m.email}
              </div>
            </div>
            {admin && m.id !== me.id ? (
              <>
                <select
                  className="input"
                  style={{ width: 170, height: 34 }}
                  value={m.role}
                  onChange={(e) =>
                    api
                      .patch(`/orgs/${org.id}/members/${m.id}`, { role: e.target.value })
                      .then(reload)
                      .catch(report)
                  }
                >
                  <option value="member">{t('org.roles.member')}</option>
                  <option value="admin">{t('org.roles.admin')}</option>
                  <option value="owner">{t('org.roles.owner')}</option>
                </select>
                <button
                  className="icon-btn sm"
                  aria-label={t('common.remove')}
                  onClick={() =>
                    api
                      .del(`/orgs/${org.id}/members/${m.id}`)
                      .then(reload)
                      .catch(report)
                  }
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <span className="badge">{t(`org.roles.${m.role}`)}</span>
            )}
          </div>
        ))}
        {admin && !!members.data?.invites.length && (
          <>
            <div className="menu-label" style={{ padding: '16px 0 4px' }}>
              {t('org.pending')}
            </div>
            {members.data.invites.map((i) => (
              <div className="person-row" key={i.id}>
                <div className="who">{i.email}</div>
                <span className="badge">{t(`org.roles.${i.role}`)}</span>
                <button
                  className="icon-btn sm"
                  aria-label={t('common.remove')}
                  onClick={() =>
                    api
                      .del(`/orgs/${org.id}/invites/${i.id}`)
                      .then(reload)
                      .catch(report)
                  }
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="settings-card">
        <h3>{t('org.teams')}</h3>
        {teams.data?.map((tm) => (
          <div className="person-row" key={tm.id}>
            <span className="ws-icon" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
              <Users size={16} />
            </span>
            <div className="who">
              <div style={{ fontWeight: 600 }}>{tm.name}</div>
              <div className="subtle" style={{ fontSize: 'var(--fs-2)' }}>
                {t('common.members')}: {tm.members} · {t('dashboard.boardsCount', { count: tm.boards })}
              </div>
            </div>
            {tm.joined ? (
              <button className="btn sm" onClick={() => setParams({ org: org.id, team: tm.id })}>
                {t('common.open')}
              </button>
            ) : (
              <button
                className="btn sm primary"
                onClick={() =>
                  api
                    .post(`/teams/${tm.id}/join`)
                    .then(() => {
                      void qc.invalidateQueries({ queryKey: ['org-teams', org.id] });
                      void refresh();
                    })
                    .catch(report)
                }
              >
                {t('dashboard.joinTeam')}
              </button>
            )}
          </div>
        ))}
      </div>

      {admin && <OrgGuests org={org} />}

      <OrgAgents org={org} />

      {(org.role === 'owner' || org.role === 'admin') && (
        <div className="settings-card">
          <h3>{t('activity.title')}</h3>
          <p className="subtle" style={{ margin: '0 0 10px', fontSize: 'var(--fs-3)' }}>
            {t('activity.hint')}
          </p>
          <ActivityLog path={`/orgs/${org.id}/activity`} />
        </div>
      )}

      <button
        className="btn danger"
        onClick={() =>
          api
            .del(`/orgs/${org.id}/members/${me.id}`)
            .then(async () => {
              await refresh();
              setParams({});
            })
            .catch(report)
        }
      >
        {t('org.leave')}
      </button>
    </>
  );
}
